import { useState } from 'react';
import type { FormEvent } from 'react';

// Matches the backend's featureName schema exactly (feature-flags.validator.ts),
// checked client-side so a malformed name never round-trips before failing.
const FEATURE_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const MAX_FEATURE_NAME_LENGTH = 100;
// Leading underscore is impossible under FEATURE_NAME_PATTERN, so this sentinel
// can never collide with a real feature key.
const OTHER_OPTION_VALUE = '__other__';

export interface FeatureOption {
    value: string;
    label: string;
}

interface AddBetaFeatureFormProps {
    options: FeatureOption[];
    disabled: boolean;
    onAdd: (feature: string) => void | Promise<void>;
}

export function AddBetaFeatureForm({ options, disabled, onAdd }: AddBetaFeatureFormProps) {
    const hasOptions = options.length > 0;
    const [selected, setSelected] = useState(hasOptions ? '' : OTHER_OPTION_VALUE);
    const [customValue, setCustomValue] = useState('');
    const [error, setError] = useState<string | null>(null);
    const isOther = selected === OTHER_OPTION_VALUE;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        const feature = isOther ? customValue.trim() : selected;
        if (!feature) return;
        if (isOther) {
            if (feature.length > MAX_FEATURE_NAME_LENGTH) {
                setError(`Must be ${MAX_FEATURE_NAME_LENGTH} characters or fewer.`);
                return;
            }
            if (!FEATURE_NAME_PATTERN.test(feature)) {
                setError(
                    'Must start with a letter and contain only letters, numbers, hyphens, and underscores.'
                );
                return;
            }
        }
        setError(null);
        await onAdd(feature);
        setCustomValue('');
        setSelected(hasOptions ? '' : OTHER_OPTION_VALUE);
    };

    const canSubmit = isOther ? customValue.trim().length > 0 : selected.length > 0;

    return (
        <form onSubmit={(e) => void handleSubmit(e)} className="flex items-start gap-2.5">
            <div className="flex-1 min-w-0 flex flex-col gap-1">
                <select
                    value={selected}
                    disabled={disabled}
                    onChange={(e) => {
                        setSelected(e.target.value);
                        setError(null);
                    }}
                    aria-label="Beta feature"
                    className="border border-gray-300 rounded-md h-9 px-3 text-sm text-gray-800 w-full disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {hasOptions && (
                        <option value="" disabled>
                            Select a feature
                        </option>
                    )}
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                    <option value={OTHER_OPTION_VALUE}>Other...</option>
                </select>
                {isOther && (
                    <input
                        type="text"
                        value={customValue}
                        disabled={disabled}
                        onChange={(e) => {
                            setCustomValue(e.target.value);
                            setError(null);
                        }}
                        placeholder="Feature name"
                        aria-label="Feature name"
                        maxLength={MAX_FEATURE_NAME_LENGTH}
                        className="border border-gray-300 rounded-md h-9 px-3 text-sm text-gray-800 w-full disabled:cursor-not-allowed disabled:opacity-50"
                    />
                )}
                {error && <span className="text-xs text-red-600">{error}</span>}
            </div>
            <button
                type="submit"
                disabled={disabled || !canSubmit}
                className="bg-gray-900 text-white font-semibold px-4 h-9 rounded-lg text-sm hover:bg-black transition-colors disabled:opacity-50 shrink-0"
            >
                Add
            </button>
        </form>
    );
}
